import React from 'react';

interface PaginationProps {
  currentPage: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (itemsPerPage: number) => void;
  pageSizeOptions?: number[];
}

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalItems,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  pageSizeOptions = [10, 20, 30, 50, 100]
}) => {
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startItem = totalItems > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const generatePageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      // Show all pages if total pages is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show first page
      pages.push(1);
      
      if (currentPage > 3) {
        pages.push('...');
      }
      
      // Show pages around current page
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      if (currentPage < totalPages - 2) {
        pages.push('...');
      }
      
      // Show last page
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      onPageChange(page);
    }
  };

  if (totalItems === 0) {
    return null;
  }

  return (
    <div className="pagination-container">
      <div className="pagination-info">
        <div className="items-info">
          Showing <strong>{startItem}</strong> to <strong>{endItem}</strong> of <strong>{totalItems}</strong> results
        </div>
        
        <div className="page-size-selector">
          <label htmlFor="pageSize">Items per page:</label>
          <select
            id="pageSize"
            value={itemsPerPage}
            onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
            className="page-size-select"
          >
            {pageSizeOptions.map(size => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>
      </div>

      {totalPages > 1 && (
        <div className="pagination-controls">
          {/* Previous Button */}
          <button
            className={`page-btn ${currentPage === 1 ? 'disabled' : ''}`}
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            title="Previous page"
          >
            ‹ Previous
          </button>

          {/* Page Numbers */}
          <div className="page-numbers">
            {generatePageNumbers().map((page, index) => (
              <React.Fragment key={index}>
                {typeof page === 'number' ? (
                  <button
                    className={`page-number ${page === currentPage ? 'active' : ''}`}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </button>
                ) : (
                  <span className="page-ellipsis">{page}</span>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Next Button */}
          <button
            className={`page-btn ${currentPage === totalPages ? 'disabled' : ''}`}
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            title="Next page"
          >
            Next ›
          </button>
        </div>
      )}

      <style>{`
        .pagination-container {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          padding: 1.5rem;
          background: var(--bg-card);
          border-top: 1px solid var(--border-primary);
          align-items: center;
        }

        .pagination-info {
          display: flex;
          justify-content: space-between;
          align-items: center;
          width: 100%;
          gap: 1rem;
        }

        .items-info {
          color: var(--text-secondary);
          font-size: 0.875rem;
        }

        .page-size-selector {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: var(--text-secondary);
          font-size: 0.875rem;
        }

        .page-size-select {
          padding: 0.375rem 0.75rem;
          border: 1px solid var(--border-primary);
          border-radius: 4px;
          background: var(--bg-primary);
          color: var(--text-primary);
          font-size: 0.875rem;
          cursor: pointer;
        }

        .page-size-select:focus {
          outline: none;
          border-color: var(--primary);
        }

        .pagination-controls {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .page-btn {
          padding: 0.5rem 1rem;
          border: 1px solid var(--border-primary);
          background: var(--bg-primary);
          color: var(--text-primary);
          border-radius: 4px;
          cursor: pointer;
          font-size: 0.875rem;
          transition: all 0.2s;
        }

        .page-btn:hover:not(.disabled) {
          background: var(--primary-light);
          border-color: var(--primary);
          color: var(--primary);
        }

        .page-btn.disabled {
          opacity: 0.5;
          cursor: not-allowed;
          background: var(--bg-secondary);
        }

        .page-numbers {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          margin: 0 1rem;
        }

        .page-number {
          min-width: 2.5rem;
          height: 2.5rem;
          border: 1px solid var(--border-primary);
          background: var(--bg-primary);
          color: var(--text-primary);
          border-radius: 4px;
          cursor: pointer;
          font-size: 0.875rem;
          font-weight: 500;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .page-number:hover {
          background: var(--primary-light);
          border-color: var(--primary);
          color: var(--primary);
        }

        .page-number.active {
          background: var(--primary);
          border-color: var(--primary);
          color: white;
        }

        .page-ellipsis {
          padding: 0.5rem 0.25rem;
          color: var(--text-secondary);
          font-size: 0.875rem;
        }

        @media (max-width: 768px) {
          .pagination-container {
            padding: 1rem;
          }

          .pagination-info {
            flex-direction: column;
            align-items: stretch;
            text-align: center;
          }

          .pagination-controls {
            flex-wrap: wrap;
            justify-content: center;
          }

          .page-numbers {
            margin: 0 0.5rem;
          }

          .page-btn {
            padding: 0.375rem 0.75rem;
            font-size: 0.8125rem;
          }

          .page-number {
            min-width: 2.25rem;
            height: 2.25rem;
            font-size: 0.8125rem;
          }
        }

        @media (max-width: 480px) {
          .page-numbers {
            display: none;
          }

          .pagination-controls::after {
            content: "Page " attr(data-current) " of " attr(data-total);
            color: var(--text-secondary);
            font-size: 0.875rem;
            margin: 0 1rem;
          }
        }
      `}</style>
    </div>
  );
};